import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    
    // Parse the JSON response
    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Error al iniciar sesión');
    }
    
    setUser(data.user);
    return data;
  };

  const register = async (email, password, name, workshopName) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name, workshop_name: workshopName })
    });
    
    // Parse the JSON response
    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Error al registrarse');
    }
    
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const exchangeSession = async (sessionId) => {
    const response = await fetch(`${API_URL}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });
    
    // Parse the JSON response
    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    
    if (!response.ok) {
      throw new Error('Error al autenticar con Google');
    }
    
    setUser(data.user);
    return data;
  };

  const updateProfile = async (profileData) => {
    const response = await fetch(`${API_URL}/api/auth/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(profileData)
    });
    
    // Parse the JSON response
    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    
    if (!response.ok) {
      throw new Error('Error al actualizar perfil');
    }
    
    setUser(data);
    return data;
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const isPro = user?.subscription_plan === 'pro' || user?.subscription_plan === 'trial';
  const isTrial = user?.subscription_plan === 'trial';
  
  const trialDaysLeft = user?.subscription_plan === 'trial' && user?.subscription_expires_at 
    ? Math.max(0, Math.ceil((new Date(user.subscription_expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      exchangeSession,
      updateProfile,
      refreshUser,
      isPro,
      isTrial,
      trialDaysLeft,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};
